import React, { useCallback, useState } from "react";

import { describeVideoCommentError } from "../../../Hooks/Comments/Video/useVideoComments";
import {
    ResolveVideoCommentReportRequestDTO,
    VideoCommentModerationDecision,
    VideoCommentModerationReportDTO,
} from "../../../Models/DTOs/VideoCommentsDTO";
import CommentDialog from "./CommentDialog";

interface ResolveVideoCommentReportDialogProps {
    report: VideoCommentModerationReportDTO;
    decision: VideoCommentModerationDecision;
    onClose: () => void;
    onResolve: (request: ResolveVideoCommentReportRequestDTO) => Promise<unknown>;
}

const ResolveVideoCommentReportDialog: React.FC<
    ResolveVideoCommentReportDialogProps
> = ({ report, decision, onClose, onResolve }) => {
    const [resolution, setResolution] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const close = useCallback(() => {
        if (!submitting) onClose();
    }, [onClose, submitting]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!resolution.trim() || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await onResolve({
                decision,
                resolution: resolution.trim(),
                ...(decision === "delete"
                    ? { comment_version: report.comment.version }
                    : {}),
            });
            onClose();
        } catch (submitError) {
            setError(describeVideoCommentError(submitError));
        } finally {
            setSubmitting(false);
        }
    };

    const deleting = decision === "delete";
    return (
        <CommentDialog
            title={deleting ? "Delete comment and resolve reports?" : "Dismiss report?"}
            description={
                deleting
                    ? "The comment will be soft-deleted and all open reports for it will be actioned."
                    : "The comment will remain visible and this report will be dismissed."
            }
            onClose={close}
        >
            <form className="video-comments__dialog-form" onSubmit={submit}>
                <label>
                    Resolution
                    <textarea
                        rows={5}
                        value={resolution}
                        maxLength={1000}
                        required
                        onChange={(event) => setResolution(event.target.value)}
                    />
                </label>
                <span className="video-comments__field-count">
                    {resolution.length.toLocaleString()}/1,000
                </span>
                {error && (
                    <p className="video-comments__form-error" role="alert">
                        {error}
                    </p>
                )}
                <div className="video-comments__dialog-actions">
                    <button type="button" className="is-secondary" onClick={close}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={deleting ? "is-danger" : ""}
                        disabled={!resolution.trim() || submitting}
                    >
                        {submitting
                            ? "Saving…"
                            : deleting
                            ? "Delete and resolve"
                            : "Dismiss report"}
                    </button>
                </div>
            </form>
        </CommentDialog>
    );
};

export default ResolveVideoCommentReportDialog;
